import { Box, Link, Typography } from "@mui/material";
import { memo } from "react";
import { useNavigate } from "react-router";
import { toSlug } from "../../../utils/create-slug";
import { useAnalytics } from "../../analytics/analytics";
import { Carousel } from "./carousel";
import { DiscoverCard } from "./discover-card";

import type { BrandTone } from "../../brand-kit/server/brand-tone-service";
import type { BrandInfoSelected } from "../../brand-kit/types";
import type { LibraryTemplate } from "../library/types";
import type { DiscoverTemplate } from "../types";
import type { CarouselItemProps } from "./carousel";
import type { FC } from "react";

export const DiscoverCarousel: FC<{
  title: string;
  loading?: boolean;
  templates: LibraryTemplate[];
  brandTone?: BrandTone;
  brandKitSelections?: BrandInfoSelected;
  Badge?: FC<{
    template: DiscoverTemplate;
    index: number;
  }>;
  slug?: string;
  subheadingGetter?: (t: DiscoverTemplate) => string | null;
}> = ({ title, templates, brandTone, brandKitSelections, Badge, slug, subheadingGetter }) => {
  const titleTestId = `carousel-title-${toSlug(title)}`;

  if (templates.length === 0) {
    return null;
  }

  return (
    <Box sx={{ minHeight: "450px", "& .Carousel-scrollContainer": { paddingLeft: "20px" } }}>
      {slug ? (
        <Link href={`/discover/${slug}`} sx={{ color: "text.primary" }}>
          <Typography variant="h5" data-testid={titleTestId}>
            {title}
          </Typography>
        </Link>
      ) : (
        <Typography variant="h5" data-testid={titleTestId} sx={{ fontWeight: 600, mb: 2 }}>
          {title}
        </Typography>
      )}

      <Carousel<DiscoverTemplate & { key: string }, ExtraProps>
        items={templates.map((template) => ({
          ...template,
          key: template.slug,
        }))}
        Item={Item}
        ItemExtraProps={{
          brandTone,
          brandKitSelections,
          Badge,
          subheadingGetter,
          carouselTitle: title,
          carouselSlug: `carousel-${title.replace(" ", "-").toLowerCase()}`,
        }}
        itemWidth="calc(max(min(300px, 20%), 250px))"
        sx={{
          "& .Carousel-scrollContainer": {
            pt: "15px", // to account for zoom on hover
          },
        }}
      />
    </Box>
  );
};

interface ExtraProps {
  Badge?: FC<{
    template: DiscoverTemplate;
    index: number;
  }>;
  brandTone?: BrandTone;
  brandKitSelections?: BrandInfoSelected;
  subheadingGetter?: (t: DiscoverTemplate) => string | null;
  carouselTitle: string;
  carouselSlug: string;
}

// oxlint-disable-next-line typescript/no-unsafe-type-assertion
const NO_EXTRA_PROPS = {} as ExtraProps;

const Item = memo(
  ({
    value: template,
    i,
    extraProps: {
      Badge,
      brandTone,
      brandKitSelections,
      subheadingGetter,
      carouselTitle,
      carouselSlug,
    } = NO_EXTRA_PROPS,
  }: CarouselItemProps<DiscoverTemplate, ExtraProps | undefined>) => {
    const navigate = useNavigate();
    const { gaEvent } = useAnalytics();

    const handleClick = (): void => {
      gaEvent("discover_carousel", {
        action: "click",
        type: carouselSlug,
        value: template.title,
      });

      const pathname = `/discover/${template.program.slug}/${template.slug}`;
      void navigate({
        pathname,
        ...(brandTone
          ? {
              search: `?${new URLSearchParams({
                tone: brandTone?.id,
              }).toString()}`,
            }
          : {}),
      });
    };

    return (
      <DiscoverCard
        template={template}
        onClick={handleClick}
        brandKitSelections={brandKitSelections}
        data-testid={carouselTitle + template.slug}
        sx={{
          overflowY: "visible",
          marginRight: 2,
        }}
        badge={Badge && <Badge template={template} index={i} />}
        subheadingGetter={subheadingGetter}
      />
    );
  },
);
